import React from 'react';
import useReveal, { reveal } from '../hooks/useReveal';
import { TESTIMONIALS } from '../data/site';

/*
  A quote wall rather than a rotating carousel: every quote is readable at once,
  nothing moves on a timer, and there is no hidden content behind dots.

  The quotes, names, roles and schools are carried over from the previous site
  unchanged. Attribution is always name plus role plus school.
*/
const Testimonials = () => {
  const [ref, isVisible] = useReveal();

  return (
    <section id="testimonials" className="ab-section ab-quotes" ref={ref}>
      <div className="ab-shell">
        <header className={`ab-quotes__head ${reveal(isVisible)}`}>
          <h2 className="ab-h2">What schools tell us</h2>
        </header>

        <div className={`ab-quotes__wall ${reveal(isVisible)}`} style={{ '--ab-reveal-delay': '80ms' }}>
          {TESTIMONIALS.map((item) => (
            <figure key={item.name} className="ab-quotes__item">
              <blockquote>
                <p>{item.quote}</p>
              </blockquote>
              <figcaption>
                <span className="ab-quotes__name">{item.name}</span>
                <span className="ab-quotes__meta">
                  {item.role}, {item.school}
                </span>
                <span className="ab-quotes__meta">{item.location}</span>
              </figcaption>
            </figure>
          ))}
        </div>
      </div>

      <style jsx>{`
        .ab-quotes__head { margin-bottom: 2.25rem; }

        /*
          CSS columns give the wall variable card heights without forcing every
          quote to the length of the longest one.
        */
        .ab-quotes__wall {
          columns: 3;
          column-gap: 1rem;
        }

        .ab-quotes__item {
          break-inside: avoid;
          margin: 0 0 1rem;
          padding: 1.5rem;
          border: 1px solid var(--ab-line);
          border-radius: var(--ab-r-card);
          background: var(--ab-paper);
          transition: border-color 0.25s var(--ab-ease), box-shadow 0.25s var(--ab-ease);
        }

        .ab-quotes__item:hover {
          border-color: var(--ab-line-strong);
          box-shadow: var(--ab-shadow-sm);
        }

        .ab-quotes__item blockquote { margin: 0; }

        .ab-quotes__item blockquote p {
          font-size: 0.9375rem;
          line-height: 1.6;
          color: var(--ab-ink);
        }

        .ab-quotes__item blockquote p::before { content: '\\201C'; }
        .ab-quotes__item blockquote p::after { content: '\\201D'; }

        .ab-quotes__item figcaption {
          margin-top: 1.125rem;
          padding-top: 1rem;
          border-top: 1px solid var(--ab-line);
        }

        .ab-quotes__name {
          display: block;
          font-family: var(--ab-font-display);
          font-size: 0.9375rem;
          font-weight: 600;
          letter-spacing: -0.015em;
          color: var(--ab-ink);
        }

        .ab-quotes__meta {
          display: block;
          font-size: 0.8125rem;
          line-height: 1.45;
          color: var(--ab-ink-3);
        }

        @media (max-width: 940px) {
          .ab-quotes__wall { columns: 2; }
        }

        @media (max-width: 600px) {
          .ab-quotes__wall { columns: 1; }
        }
      `}</style>
    </section>
  );
};

export default Testimonials;
